import React, { useMemo } from 'react';

import ColoredLabel from '@uikit/old/ColoredLabel/ColoredLabel';
import { isAppRealm, isConciergeRealm, isPublisherRealm } from '@utils/realmIdentifier';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';
import { useQuery } from 'react-query';
import { NotificationCounterType } from '@ts/userOrganization.types';
import { getNotificationCountersApi } from '@api/userNotification.api';
import SidebarTrialCounter from '../Sidebar/components/SidebarTrialCounter/SidebarTrialCounter';

declare const IS_DEV: boolean;

export default [
  {
    key: 'account',
    icon: 'gear',
    color: 'orange',
    positionElements: 'column',
    Children: () => <SidebarTrialCounter />,
  },
  {
    key: 'billing',
    icon: 'wallet',
    positionElements: 'column',
    Children: ({ renderProps }) => {
      const credits = renderProps?.credits ?? ({} as any);

      const toNumberOrNull = (v: unknown): number | null => {
        if (typeof v === 'number') return v;
        if (v != null && v !== '') {
          const n = Number(v);
          return Number.isFinite(n) ? n : null;
        }
        return null;
      };

      const paid = toNumberOrNull(credits.currentPaidCreditsNumber);
      const bonus = toNumberOrNull(credits.currentBonusCreditsNumber);
      const rawBalance = credits.balance ?? credits.amount;
      const balance = toNumberOrNull(rawBalance);

      const availableFunds = toNumberOrNull(credits.availableFunds);

      const hasAnyCredits = paid != null || bonus != null;
      const creditsSum = (paid ?? 0) + (bonus ?? 0);

      const formatCompact = (n: number): string => {
        if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`;
        if (n >= 1_000) return `${(n / 1000).toFixed(1)}k`;
        return `${n}`;
      };

      const formatMoneyCompact = (n: number): string => {
        if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}m`;
        if (n >= 1_000) return `$${(n / 1000).toFixed(1)}k`;
        return `$${n}`;
      };

      function composeBalance(): string {
        if (isPublisherRealm()) {
          if (availableFunds != null) return formatMoneyCompact(availableFunds);
          if (balance != null) return formatMoneyCompact(balance);
          return '-';
        }

        if (isAppRealm()) {
          if (hasAnyCredits) {
            return formatCompact(creditsSum);
          }
          if (balance != null) {
            return formatMoneyCompact(balance);
          }
          return '-';
        }

        if (balance != null) {
          return formatMoneyCompact(balance);
        }
        if (hasAnyCredits) {
          return formatCompact(creditsSum);
        }
        return '-';
      }

      return (
        <ColoredLabel color="blue" fontSize="small" position="moved-to-top">
          {composeBalance()}
        </ColoredLabel>
      );
    },
    Tooltip({ renderProps }): JSX.Element {
      const credits = renderProps?.credits ?? ({} as any);

      const toNumberOrNull = (v: unknown): number | null => {
        if (typeof v === 'number') return v;
        if (v != null && v !== '') {
          const n = Number(v);
          return Number.isFinite(n) ? n : null;
        }
        return null;
      };

      const paid = toNumberOrNull(credits.currentPaidCreditsNumber);
      const bonus = toNumberOrNull(credits.currentBonusCreditsNumber);
      const rawBalance = credits.balance ?? credits.amount;
      const balance = toNumberOrNull(rawBalance);
      const availableFunds = toNumberOrNull(credits.availableFunds);
      const pendingFunds = toNumberOrNull(credits.pendingFunds);

      const hasAnyCredits = paid != null || bonus != null;
      const creditsSum = (paid ?? 0) + (bonus ?? 0);

      // Realm-based tooltip content
      if (isPublisherRealm()) {
        return (
          <div>
            <div>Available: {availableFunds != null ? `$${availableFunds}` : '-'}</div>
            <div>Pending: {pendingFunds != null ? `$${pendingFunds}` : '-'}</div>
          </div>
        );
      }

      if (hasAnyCredits) {
        return <div>{creditsSum} credits</div>;
      }
      if (balance != null) {
        return <div>${balance}</div>;
      }
      return <div>-</div>;
    },
  },
  ...(IS_DEV || isConciergeRealm() || isPublisherRealm()
    ? [
        {
          key: 'notifications',
          icon: 'notificationsIcon',
          color: 'blue',
          positionElements: 'column',
          Children: (): JSX.Element => {
            const currentWorkspaceId = useCurrentWorkspaceId();
            const key = useMemo(
              () => ['workspace-notifications-counter', currentWorkspaceId],
              [currentWorkspaceId]
            );

            const { data } = useQuery<NotificationCounterType>(
              key,
              () => getNotificationCountersApi(currentWorkspaceId),
              {
                enabled: !!currentWorkspaceId,
                retry: 1,
                refetchOnWindowFocus: true,
                staleTime: 5 * 60 * 1000,
                refetchInterval: 5 * 60 * 1000,
                refetchIntervalInBackground: true,
              }
            );

            if (data == null) {
              return null;
            }

            if (data.notViewedItems > 0) {
              return (
                <ColoredLabel color="red" fontSize="small" position="moved-to-top">
                  <span>{data.notViewedItems}</span>
                </ColoredLabel>
              );
            }

            return (
              <ColoredLabel color="blue" fontSize="small" position="moved-to-top">
                <span>{data.totalItems}</span>
              </ColoredLabel>
            );
          },
          Tooltip(): JSX.Element {
            return <div>Notifications</div>;
          },
        },
      ]
    : []),
];
